import type { DomainGuide } from '@/components/guide/guide-types';

import th from './th.json';
import en from './en.json';
import zh from './zh.json';
import vi from './vi.json';
import km from './km.json';
import lo from './lo.json';
import my from './my.json';

/**
 * Help catalog for the `content` domain — the four tabs of `/content`.
 *
 * Two of the four screens do less than they look like they do, and the
 * statuses say so:
 *
 *   announcements — the notice really reaches the lobby, but the `audience`
 *     control is stored and never read (only `target_chain` decides who sees
 *     a row), so one of the form's two targeting controls is decorative.
 *   banners — nothing to disclaim. It has no `audience` column at all:
 *     `displayContext` is the only targeting control and the lobby honours it.
 *   popups — nothing to disclaim either, since `audience` was retired. Every
 *     control the form offers is read by the lobby; `displayContext` decides
 *     when the creative appears, as it does for banners.
 *   branding — `setting.site_brandings` has exactly one consumer in the whole
 *     monorepo: this tab. The lobby's name/logo/favicon/colours come from
 *     svc-tenancy `/site-config`, i.e. the org's white-label fields.
 */
export const contentGuide: DomainGuide = {
  domain: 'content',
  pages: [
    {
      id: 'content.announcements',
      route: '/content → Announcements',
      permission: 'notify:read',
      status: 'partially-wired',
      fields: [
        { key: 'title', kind: 'text' },
        { key: 'body', kind: 'text' },
        { key: 'audience', kind: 'status' },
        { key: 'target', kind: 'text' },
        { key: 'pinned', kind: 'status' },
        { key: 'publishFrom', kind: 'date' },
        { key: 'publishTo', kind: 'date' },
        { key: 'status', kind: 'status' },
        { key: 'view', kind: 'status' },
      ],
      cautionCount: 5,
      warningCount: 1,
    },
    {
      id: 'content.banners',
      route: '/content → Banners',
      permission: 'notify:read',
      status: 'live',
      fields: [
        { key: 'name', kind: 'text' },
        { key: 'imageUrl', kind: 'text' },
        { key: 'linkUrl', kind: 'text' },
        { key: 'title', kind: 'text' },
        { key: 'subtitle', kind: 'text' },
        { key: 'description', kind: 'text' },
        { key: 'displayContext', kind: 'status' },
        { key: 'displayFrom', kind: 'date' },
        { key: 'displayTo', kind: 'date' },
        { key: 'priority', kind: 'count' },
        { key: 'status', kind: 'status' },
      ],
      cautionCount: 5,
      warningCount: 1,
    },
    {
      id: 'content.popups',
      route: '/content → Popups',
      permission: 'notify:read',
      status: 'live',
      fields: [
        { key: 'name', kind: 'text' },
        { key: 'imageUrl', kind: 'text' },
        { key: 'linkUrl', kind: 'text' },
        { key: 'displayContext', kind: 'status' },
        { key: 'displayFrom', kind: 'date' },
        { key: 'displayTo', kind: 'date' },
        { key: 'priority', kind: 'count' },
        { key: 'maxShowsPerDay', kind: 'count' },
        { key: 'status', kind: 'status' },
      ],
      cautionCount: 6,
      warningCount: 1,
    },
    {
      id: 'content.branding',
      route: '/content → Branding',
      permission: 'setting:update',
      status: 'records-intent',
      fields: [
        { key: 'displayName', kind: 'text' },
        { key: 'logoUrl', kind: 'text' },
        { key: 'faviconUrl', kind: 'text' },
        { key: 'primaryColor', kind: 'text' },
        { key: 'secondaryColor', kind: 'text' },
        { key: 'domains', kind: 'text' },
      ],
      cautionCount: 5,
      warningCount: 0,
    },
  ],
  copy: { th, en, zh, vi, km, lo, my },
};
